import { STRATEGY_KEYS, type ExecutionMode, type ReportGranularity, type ReportResponse, type ReportTrade, type StrategyKey } from '@et/shared';
import { useMemo, useState } from 'react';
import { CoinBars, PeriodBars } from '../components/ReportCharts';
import { dateTime, money, signed, tone } from '../lib/format';
import { locale, useLang, useT } from '../lib/i18n';
import { useResource } from '../lib/use-resource';
import { useLive } from '../stores/live';
import { Coin } from '../components/CoinLogo';
import { TradeQualityCard } from '../components/Insights';

/**
 * Reports: closed trades in a date range — profit, loss, fees, what each buy cost and each
 * sell brought, the account value around it — with per-period and per-coin charts.
 * Ranges are cut in the browser's time zone and the server groups in the same zone.
 */

type Preset = 'today' | 'week' | 'month' | '30d' | '90d' | 'all' | 'custom';
const PRESETS: Preset[] = ['today', 'week', 'month', '30d', '90d', 'all', 'custom'];
const PRESET_LABEL: Record<Preset, string> = {
  today: 'Today',
  week: 'This week',
  month: 'This month',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  all: 'All time',
  custom: 'Date range',
};
const GRANULARITY_LABEL: Record<ReportGranularity, string> = { day: 'Daily', week: 'Weekly', month: 'Monthly' };

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Istanbul';

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function rangeOf(preset: Preset, custom: { from: string; to: string }): { from: Date | null; to: Date | null } {
  const now = new Date();
  const today = startOfDay(now);
  switch (preset) {
    case 'today':
      return { from: today, to: null };
    case 'week': {
      const monday = new Date(today);
      monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
      return { from: monday, to: null };
    }
    case 'month':
      return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: null };
    case '30d':
      return { from: new Date(today.getTime() - 29 * 86_400_000), to: null };
    case '90d':
      return { from: new Date(today.getTime() - 89 * 86_400_000), to: null };
    case 'all':
      return { from: null, to: null };
    case 'custom': {
      const from = custom.from ? new Date(`${custom.from}T00:00:00`) : null;
      const to = custom.to ? new Date(`${custom.to}T23:59:59.999`) : null;
      return { from, to };
    }
  }
}

function periodLabel(period: string, g: ReportGranularity, weekOf: string): string {
  const [y, m, d] = period.split('-').map(Number);
  const date = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  if (g === 'month') return date.toLocaleDateString(locale(), { month: 'long', year: 'numeric' });
  const day = date.toLocaleDateString(locale(), { day: 'numeric', month: 'short' });
  return g === 'week' ? weekOf.replace('{date}', day) : day;
}

function csv(trades: ReportTrade[], t: (s: string) => string): string {
  const tr = useLang.getState().lang === 'tr';
  const num = (v: string | null): string => (v === null ? '' : tr ? v.replace('.', ',') : v);
  const head = ['Coin', 'Strategy', 'Opened', 'Closed', 'Qty', 'Buy price', 'Sell price', 'Bought for (USDT)', 'Sold for (USDT)', 'Fees (USDT)', 'Profit / loss (USDT)', '%', 'Balance at buy', 'Balance at sell', 'Reason'].map(t);
  const rows = trades.map((x) => [x.symbol, t(x.strategyKey), x.openedAt ?? '', x.closedAt, num(x.qty), num(x.entryPrice), num(x.exitPrice), num(x.boughtValue), num(x.soldValue), num(x.fees), num(x.pnl), num(x.pnlPct), num(x.equityAtEntry), num(x.equityAtExit), x.closeReason ? t(x.closeReason) : '']);
  const sep = tr ? ';' : ',';
  return '﻿' + [head, ...rows].map((r) => r.map((c) => (/[;,"\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)).join(sep)).join('\n');
}

export function Reports(): JSX.Element {
  const t = useT();
  const currentMode = useLive((s) => s.status?.mode) ?? 'paper';
  const [preset, setPreset] = useState<Preset>('30d');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [granularity, setGranularity] = useState<ReportGranularity>('day');
  const [mode, setMode] = useState<ExecutionMode | null>(null);
  const [strategy, setStrategy] = useState<StrategyKey | ''>('');
  const effectiveMode = mode ?? currentMode;

  const path = useMemo(() => {
    const r = rangeOf(preset, custom);
    const p = new URLSearchParams({ granularity, tz: TZ, mode: effectiveMode });
    if (r.from) p.set('from', r.from.toISOString());
    if (r.to) p.set('to', r.to.toISOString());
    if (strategy) p.set('strategy', strategy);
    return `/reports?${p.toString()}`;
  }, [preset, custom, granularity, effectiveMode, strategy]);
  const report = useResource<ReportResponse>(path);
  const qualityQuery = useMemo(() => {
    const r = rangeOf(preset, custom);
    const p = new URLSearchParams({ mode: effectiveMode });
    if (r.from) p.set('from', r.from.toISOString());
    if (r.to) p.set('to', r.to.toISOString());
    if (strategy) p.set('strategy', strategy);
    return p.toString();
  }, [preset, custom, effectiveMode, strategy]);
  const d = report.data;
  const winRate = d && d.totals.trades > 0 ? ((d.totals.wins / d.totals.trades) * 100).toFixed(1) : null;

  const download = (): void => {
    if (!d) return;
    const url = URL.createObjectURL(new Blob([csv(d.trades, t)], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `et-tradebot-${effectiveMode}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="stack">
      <h1>{t('Reports')}</h1>

      <div className="card stack">
        <div className="row" role="group" aria-label={t('Date range')}>
          {PRESETS.map((p) => <button key={p} className={`small ${p === preset ? 'primary' : ''}`} onClick={() => { setPreset(p); }}>{t(PRESET_LABEL[p])}</button>)}
        </div>
        {preset === 'custom' && (
          <div className="row">
            <label className="field">{t('From')}<input type="date" value={custom.from} onChange={(e) => { setCustom({ ...custom, from: e.target.value }); }} /></label>
            <label className="field">{t('To')}<input type="date" value={custom.to} onChange={(e) => { setCustom({ ...custom, to: e.target.value }); }} /></label>
          </div>
        )}
        <div className="row">
          <span className="muted">{t('Group charts by')}:</span>
          {(['day', 'week', 'month'] as const).map((g) => <button key={g} className={`small ${g === granularity ? 'primary' : ''}`} onClick={() => { setGranularity(g); }}>{t(GRANULARITY_LABEL[g])}</button>)}
          <span style={{ flex: 1 }} />
          <span className="muted">{t('Strategy')}:</span>
          <select value={strategy} onChange={(e) => { setStrategy(e.target.value as StrategyKey | ''); }}>
            <option value="">{t('All strategies')}</option>
            {STRATEGY_KEYS.map((k) => <option key={k} value={k}>{t(k)}</option>)}
          </select>
          <span className="muted">{t('Account')}:</span>
          <select value={effectiveMode} onChange={(e) => { setMode(e.target.value as ExecutionMode); }}>
            <option value="paper">{t('PAPER')}</option>
            <option value="live">{t('LIVE — real money')}</option>
          </select>
        </div>
      </div>

      {report.error && <div className="neg">{report.error}</div>}

      {d && (
        <>
          <div className="grid tiles">
            <div className="card tile"><div className="label">{t('Total gained')}</div><div className="value pos">{signed(d.totals.profit)}</div><div className="muted">{t('{n} winning trades', { n: d.totals.wins })}</div></div>
            <div className="card tile"><div className="label">{t('Total lost')}</div><div className="value neg">{money(d.totals.loss)}</div><div className="muted">{t('{n} losing trades', { n: d.totals.losses })}</div></div>
            <div className="card tile"><div className="label">{t('Net result')}</div><div className={`value ${tone(d.totals.net)}`}>{signed(d.totals.net)}</div><div className="muted">USDT · {t('fees included')}</div></div>
            <div className="card tile"><div className="label">{t('Fees paid')}</div><div className="value">{money(d.totals.fees)}</div><div className="muted">USDT</div></div>
            <div className="card tile"><div className="label">{t('Trades')}</div><div className="value">{d.totals.trades}</div><div className="muted">{winRate === null ? '—' : t('win rate {p}%', { p: money(winRate, 1) })}</div></div>
            <div className="card tile"><div className="label">{t('Bought / sold total')}</div><div className="value" style={{ fontSize: 16 }}>{money(d.totals.boughtValue)} / {money(d.totals.soldValue)}</div><div className="muted">USDT</div></div>
          </div>

          {!strategy && d.byStrategy.length > 0 && (
            <div className="card">
              <h2>{t('Which strategy did better')}</h2>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>{t('Strategy')}</th><th className="num">{t('Trades')}</th><th className="num">{t('Win rate')}</th><th className="num">{t('Total gained')}</th><th className="num">{t('Total lost')}</th><th className="num">{t('Fees')}</th><th className="num">{t('Net result')}</th></tr>
                  </thead>
                  <tbody>
                    {d.byStrategy.map((g) => (
                      <tr key={g.strategyKey}>
                        <td><b>{t(g.strategyKey)}</b></td>
                        <td className="num">{g.trades}</td>
                        <td className="num">{g.trades > 0 ? `${money(((g.wins / g.trades) * 100).toFixed(1), 1)}%` : '—'}</td>
                        <td className="num pos">{signed(g.profit)}</td>
                        <td className="num neg">{money(g.loss)}</td>
                        <td className="num muted">{money(g.fees)}</td>
                        <td className={`num ${tone(g.net)}`}><b>{signed(g.net)}</b></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="grid cols-2">
            <div className="card">
              <h2>{t('Net result per {period}', { period: t(granularity === 'day' ? 'day' : granularity === 'week' ? 'week' : 'month') })}</h2>
              <PeriodBars rows={d.byPeriod} labelOf={(p) => periodLabel(p, granularity, t('week of {date}'))} />
            </div>
            <div className="card">
              <h2>{t('Which coin earned or lost how much')}</h2>
              <CoinBars rows={d.bySymbol} />
            </div>
          </div>

          <TradeQualityCard query={qualityQuery} />

          <div className="card">
            <div className="row" style={{ marginBottom: 12 }}>
              <h2 style={{ margin: 0 }}>{t('Closed trades')}</h2>
              <span className="muted">{d.truncated ? t('newest 500 shown; totals include all') : t('{n} trades', { n: d.trades.length })}</span>
              <span style={{ flex: 1 }} />
              <button className="small" disabled={d.trades.length === 0} onClick={download}>{t('Download CSV')}</button>
            </div>
            {d.trades.length === 0 ? (
              <div className="empty">{t('No closed trades in this range.')}</div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t('Coin')}</th><th>{t('Strategy')}</th><th>{t('Opened')}</th><th>{t('Closed')}</th>
                      <th className="num">{t('Bought for')}</th><th className="num">{t('Sold for')}</th><th className="num">{t('Fees')}</th>
                      <th className="num">{t('Profit / loss')}</th><th className="num">%</th>
                      <th className="num">{t('Balance at buy')}</th><th className="num">{t('Balance at sell')}</th><th>{t('Reason')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.trades.map((x) => (
                      <tr key={x.id}>
                        <td><b><Coin symbol={x.symbol} /></b></td>
                        <td className="muted">{t(x.strategyKey)}</td>
                        <td className="muted">{dateTime(x.openedAt)}</td>
                        <td className="muted">{dateTime(x.closedAt)}</td>
                        <td className="num">{money(x.boughtValue)}</td>
                        <td className="num">{money(x.soldValue)}</td>
                        <td className="num muted">{money(x.fees)}</td>
                        <td className={`num ${tone(x.pnl)}`}><b>{signed(x.pnl)}</b></td>
                        <td className={`num ${tone(x.pnlPct)}`}>{signed(x.pnlPct)}</td>
                        <td className="num muted">{money(x.equityAtEntry)}</td>
                        <td className="num muted">{money(x.equityAtExit)}</td>
                        <td className="muted">{x.closeReason ? t(x.closeReason) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
